import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, switchMap } from 'rxjs';
import { SecurityService } from '../Security.service';
import { Loader } from './loader';
import { DataSourceMeta, DocAnnotation, TextItem } from '../models';
import JSZip from 'jszip';

@Injectable()
export class LoaderService extends Loader {
    public constructor(
        private http: HttpClient,
        private securityService: SecurityService,
    ) {
        super();
    }

    public GetPdf() {
        return this.http
            .get(this.securityService.pdfUri, {
                headers: this._getHeaders(),
                observe: 'response',
                responseType: 'arraybuffer',
            })
            .pipe(
                map((r) => {
                    const type = r.headers.get('Content-Type');
                    const fileName = this._parseFilenameFromContentDisposition(
                        r.headers.get('Content-Disposition'),
                    );
                    return new File([r.body], fileName || 'unknown.pdf', { type });
                }),
            );
    }

    public GetPdfDataSource() {
        return this.http.get<DataSourceMeta[]>(this.securityService.pdfDataSourceUri, {
            headers: this._getHeaders(),
        });
    }

    public SavePdf(pdf: File) {
        const form = new FormData();
        form.append(pdf.name, pdf);

        return this.http.put<void>(this.securityService.pdfUri, form, {
            headers: this._getHeaders().set('Accept', '*/*').delete('content-type'),
        });
    }

    public GetTextItems(): Observable<TextItem[]> {
        return this.http.get<TextItem[]>(this.securityService.textItemsUri, {
            headers: this._getHeaders().set('Accept', '*/*').delete('content-type'),
        }).pipe(map(list => list.map(this.parsePdfTextStyles)));
    }

    public GetAnnotations(): Observable<DocAnnotation[]> {
        const headers = this._getHeaders().set('Accept', '*/*').delete('content-type');
        return this.http.get<DocAnnotation[]>(this.securityService.annotationsUri, { headers: headers})
        .pipe(
            switchMap((annotations) => {
            const docAnnotNums = annotations.map(a => a.docAnnotNum);

            const params = new HttpParams({ fromObject: { docAnnotNums: docAnnotNums.map(String) } });

            return this.http.get(this.securityService.annotationsUri + '/attachments', {
                headers: headers,
                params,
                responseType: 'blob'
            }).pipe(
                    switchMap(blob => this.unpackZipAndAttachToAnnotations(blob, annotations))
                );
            })
        );
    }

    public GetSelectedStampId(): string {
        return null;
    }

    public Dispose() {}

    private _parseFilenameFromContentDisposition(contentDisposition: string) {
        if (!contentDisposition) return null;
        const matches = /filename="(.*?)"/g.exec(contentDisposition);

        return matches && matches.length > 1 ? matches[1] : null;
    }

    private _getHeaders() {
        const token = this.securityService.bearerToken;
        return new HttpHeaders({
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: 'Bearer ' + token,
        });
    }

    private unpackZipAndAttachToAnnotations(blob: Blob, annotations: DocAnnotation[]): Promise<DocAnnotation[]> {
        return JSZip.loadAsync(blob).then(zip => {
            const tasks = annotations.map(annotation => {
                const fileName = `${annotation.docAnnotNum.toFixed(2)}.bin`;
                const zipEntry = zip.file(fileName);

                if (!zipEntry) return Promise.resolve();

                return zipEntry.async('blob').then(fileBlob => {
                    (annotation as any).annotFile = fileBlob;
                });
            });

            return Promise.all(tasks).then(() => annotations);
        });
    }

    private parsePdfTextStyles(item: any): TextItem {
        const result: TextItem = { ...item };

        if (item.pdfTextStyles) {
            const pdfTextStyles = JSON.parse(item.pdfTextStyles);
            result.fontColor= pdfTextStyles.fontColor;
            result.fontSize= pdfTextStyles.fontSize;
            result.fontName = pdfTextStyles.fontName;
            result.fontStyle = pdfTextStyles.fontStyle;
        }
        return result;
    }
}
